import { useEffect, useState, useContext, useCallback, useRef } from 'react';
import { db } from '../firebase';
import { auth } from '../firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import Footer from '../components/Footer';
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  getDocs,
  deleteDoc,
  updateDoc,
  getDoc,
  writeBatch
} from 'firebase/firestore';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useMyPedalsChrome } from '../contexts/MyPedalsChromeContext';
import AddPedalForm from '../components/AddPedalForm';

import { UserContext } from '../contexts/UserContext';
import { getInitiatorPedalIds } from '../utils/tradeUtils';
import { useSellingDisabled } from '../hooks/useSellingDisabled';
import { useRegisterPullToRefresh } from '../hooks/useRegisterPullToRefresh';
import { scrollPageToTop } from '../utils/scrollPageToTop';
import ListingThumb from '../components/ListingThumb';
import { placeholderFor, isPlaceholderListing, SKETCH_PLACEHOLDER_URL } from '../utils/listingPlaceholder';
import { offerModeOf, OFFER_MODE_LABELS } from '../utils/offerMode';


const COMPACT_FILTER_SELECT_STYLE = {
  borderRadius: '9999px',
  fontSize: '0.875rem',
  fontFamily: 'Nunito, sans-serif',
  fontWeight: 700,
  backgroundColor: 'white',
  color: '#374151',
  cursor: 'pointer',
  border: '1px solid #C89F3C',
  padding: '0.6rem 1.25rem 0.6rem 0.55rem',
  outline: 'none',
  appearance: 'none',
  WebkitAppearance: 'none',
  MozAppearance: 'none',
  backgroundImage: "url(\"data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%23C89F3C' d='M6 8L0 0h12z'/%3E%3C/svg%3E\")",
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 0.65rem center',
  boxSizing: 'border-box',
};

const Pedalboard = ({ onOwnsHorizontalGesture }) => {
  const { firestoreUser } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const myPedalsChrome = useMyPedalsChrome();
  const isHubView = location.pathname.includes('/my-pedals');
  const [currentUser, setCurrentUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [showEarlyBirdModal, setShowEarlyBirdModal] = useState(false);
  const [earlyBirdDaysRemaining, setEarlyBirdDaysRemaining] = useState(0);
  
  
  // Handle loading state - if we have a user but no firestore data, we're still loading
  const isLoading = currentUser && !firestoreUser;
  const isFullyVerified = currentUser?.emailVerified && firestoreUser?.phoneVerified && firestoreUser?.isVerified;
  
  console.log('🔍 PEDALBOARD VERIFICATION STATUS:', {
    currentUserExists: !!currentUser,
    emailVerified: currentUser?.emailVerified,
    firestoreUserExists: !!firestoreUser,
    phoneVerified: firestoreUser?.phoneVerified,
    isVerified: firestoreUser?.isVerified,
    isLoading,
    isFullyVerified
  });
  
  const [listings, setListings] = useState([]);
  const [listingsLoading, setListingsLoading] = useState(true);
  const [filters, setFilters] = useState({
    showActive: 'all',
    minPrice: '',
    maxPrice: ''
  });
  const [editingPedal, setEditingPedal] = useState(null);
  const [editFocus, setEditFocus] = useState(null); // 'photos' when opened via ?step=photos
  const [editReturnTo, setEditReturnTo] = useState(null); // in-app path to go back to after saving
  const { disabled: sellingDisabled } = useSellingDisabled();
  const [totalPedalValue, setTotalPedalValue] = useState(0);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [copied, setCopied] = useState(false);
  const [matchCounts, setMatchCounts] = useState({});

  // ── Bump Listings (18V feature) ───────────────────────────────────────
  const is18V = firestoreUser?.membershipTier === 'premium_plus';
  const [bumpLoading, setBumpLoading] = useState(false);
  const [bumpSuccess, setBumpSuccess] = useState(false);
  const [showBumpUpsellModal, setShowBumpUpsellModal] = useState(false);
  const [confirmDeletePedal, setConfirmDeletePedal] = useState(null);

  const getBumpCooldownInfo = () => {
    const lastBumped = firestoreUser?.lastBumpedAt;
    if (!lastBumped) return { canBump: true, daysRemaining: 0, hoursRemaining: 0 };
    const lastBumpedDate = lastBumped.toDate
      ? lastBumped.toDate()
      : new Date((lastBumped.seconds ?? 0) * 1000);
    const msRemaining = (7 * 24 * 60 * 60 * 1000) - (Date.now() - lastBumpedDate.getTime());
    if (msRemaining <= 0) return { canBump: true, daysRemaining: 0, hoursRemaining: 0 };
    return {
      canBump: false,
      daysRemaining: Math.ceil(msRemaining / (1000 * 60 * 60 * 24)),
      hoursRemaining: Math.ceil(msRemaining / (1000 * 60 * 60)),
    };
  };

  const bumpCooldown = getBumpCooldownInfo();
  const bumpIsDisabled = bumpLoading || bumpSuccess || (is18V && !bumpCooldown.canBump);

  const bumpButtonLabel = () => {
    if (bumpLoading) return 'Bumping\u2026';
    if (bumpSuccess) return '\u2713 Bumped!';
    if (!is18V) return 'Bump Listings';
    if (!bumpCooldown.canBump) {
      return bumpCooldown.daysRemaining > 1
        ? `Bump in ${bumpCooldown.daysRemaining} days`
        : `Bump in ${bumpCooldown.hoursRemaining}h`;
    }
    return 'Bump Listings';
  };
  // ─────────────────────────────────────────────────────────────────────

// Show/hide "Back to Top" button based on scroll position
useEffect(() => {
  const handleScroll = () => {
    if (window.scrollY > 300) {
      setShowBackToTop(true);
    } else {
      setShowBackToTop(false);
    }
  };

  window.addEventListener('scroll', handleScroll);
  return () => window.removeEventListener('scroll', handleScroll);
}, []);

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const handleShare = async () => {
  const shareUrl = `https://pedalxchange.com/pedalboard/${currentUser?.uid}`;
  try {
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  } catch (_) {
    // clipboard unavailable — silently fail
  }
};

const handleBumpListings = async () => {
  if (!is18V) {
    setShowBumpUpsellModal(true);
    return;
  }
  if (bumpIsDisabled) return;
  setBumpLoading(true);
  try {
    const functions = getFunctions();
    const bumpFn = httpsCallable(functions, 'bumpListings');
    const result = await bumpFn();
    if (result.data.success) {
      setBumpSuccess(true);
      setTimeout(() => setBumpSuccess(false), 4000);
    }
  } catch (err) {
    console.error('Bump failed:', err);
    alert(err.message || 'Failed to bump listings. Please try again.');
  } finally {
    setBumpLoading(false);
  }
};

// Track auth state
useEffect(() => {
  const unsubscribe = auth.onAuthStateChanged((user) => {
    setCurrentUser(user);
    setAuthLoading(false);
  });

  return () => unsubscribe();
}, []);

useEffect(() => {
  if (!isHubView || !myPedalsChrome) return undefined;

  myPedalsChrome.setRightSlot(
    <select
      className="pedalboard-filter-select my-pedals-compact-filter-select"
      value={filters.showActive}
      onChange={(e) => setFilters({ ...filters, showActive: e.target.value })}
      aria-label="Filter pedalboard listings"
      style={COMPACT_FILTER_SELECT_STYLE}
    >
      <option value="all">All</option>
      <option value="active">Active</option>
      <option value="inactive">Inactive</option>
      <option value="inTrade">In Trade</option>
      <option value="inPurchase">In Purchase</option>
    </select>
  );

  return () => myPedalsChrome.setRightSlot(null);
// eslint-disable-next-line react-hooks/exhaustive-deps -- setRightSlot is stable
}, [isHubView, myPedalsChrome?.setRightSlot, filters.showActive]);

const ADD_PEDAL_LINK_STYLE = {
  backgroundColor: 'transparent',
  border: 'none',
  padding: 0,
  fontFamily: 'Nunito, sans-serif',
  color: '#C9A227',
  fontWeight: 'bold',
  fontSize: '19px',
  textDecoration: 'none',
  whiteSpace: 'nowrap',
};

// Bump / Share sit side by side in the mobile hub, so each takes half the row.
// minWidth: 0 lets them shrink below their text width on narrow phones instead
// of pushing the row wider than the screen.
const HUB_ACTION_BTN_STYLE = {
  flex: 1,
  minWidth: 0,
  backgroundColor: 'transparent',
  borderRadius: '9999px',
  padding: '0.4rem 0.5rem',
  fontFamily: 'Nunito, sans-serif',
  fontWeight: '600',
  fontSize: '0.875rem',
  cursor: 'pointer',
  boxSizing: 'border-box',
};

// Desktop hub only: render the "+ Add a New Pedal" link inline with the
// header toggle (right-aligned). The header row it lands in is hidden on
// mobile, so this never affects the mobile layout.
useEffect(() => {
  if (!isHubView || !myPedalsChrome) return undefined;

  if (!isLoading && isFullyVerified) {
    myPedalsChrome.setAddSlot(
      <Link
        to='/addpedal'
        className='inline-block'
        style={ADD_PEDAL_LINK_STYLE}
        onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.75'; }}
        onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
      >
        + Add a New Pedal
      </Link>
    );
  } else {
    myPedalsChrome.setAddSlot(null);
  }

  return () => myPedalsChrome.setAddSlot(null);
// eslint-disable-next-line react-hooks/exhaustive-deps -- setAddSlot is stable
}, [isHubView, myPedalsChrome?.setAddSlot, isLoading, isFullyVerified]);

useEffect(() => {
  if (
    firestoreUser &&
    firestoreUser.earlyBirdEligible &&
    !firestoreUser.earlyBirdCreditAwarded &&
    !firestoreUser.earlyBirdModalShown &&
    firestoreUser.createdAt
  ) {
    const createdAt = firestoreUser.createdAt.toDate
      ? firestoreUser.createdAt.toDate()
      : new Date(firestoreUser.createdAt.seconds ? firestoreUser.createdAt.seconds * 1000 : firestoreUser.createdAt);
    const days = Math.max(0, Math.ceil(7 - ((Date.now() - createdAt.getTime()) / (1000 * 60 * 60 * 24))));
    if (days > 0) {
      setEarlyBirdDaysRemaining(days);
      setShowEarlyBirdModal(true);
    }
  }
}, [firestoreUser]);

const handleDismissEarlyBird = async () => {
  setShowEarlyBirdModal(false);
  if (auth.currentUser) {
    try {
      await updateDoc(doc(db, 'users', auth.currentUser.uid), { earlyBirdModalShown: true });
    } catch (err) {
      console.error('Failed to update earlyBirdModalShown:', err);
    }
  }
};

const handleEarlyBirdCTA = async () => {
  setShowEarlyBirdModal(false);
  if (auth.currentUser) {
    try {
      await updateDoc(doc(db, 'users', auth.currentUser.uid), { earlyBirdModalShown: true });
    } catch (err) {
      console.error('Failed to update earlyBirdModalShown:', err);
    }
  }
  navigate('/addpedal');
};

useEffect(() => {
  const fetchPedalboardValue = async () => {
    if (!auth.currentUser) return;
    try {
      const response = await getDocs(
        query(
          collection(db, 'listings'),
          where('ownerId', '==', auth.currentUser.uid),
          where('active', '==', true)
        )
      );
      let total = 0;
      response.forEach((doc) => {
        const data = doc.data();
        if (!isNaN(data.price)) {
          total += Number(data.price);
        }
      });
      setTotalPedalValue(total);
    } catch (err) {
      console.error('Failed to calculate pedalboard value:', err);
    }
  };

  fetchPedalboardValue();
}, []);

  const handleDelete = (id) => {
    const pedalToDelete = listings.find(pedal => pedal.id === id);
    if (pedalToDelete?.inTrade) {
      alert('❌ Cannot delete this pedal because it is currently involved in an active trade. Please wait for the trade to complete or be cancelled before deleting.');
      return;
    }
    setConfirmDeletePedal(pedalToDelete || { id });
  };

  const handleConfirmDelete = async () => {
    const pedal = confirmDeletePedal;
    setConfirmDeletePedal(null);
    try {
      await deleteDoc(doc(db, 'listings', pedal.id));
      console.log('✅ Pedal deleted successfully:', pedal.id);
    } catch (error) {
      console.error('❌ Error deleting listing:', error);
      alert('Failed to delete pedal. Please try again.');
    }
  };

  // Opening and closing the edit form both add or remove a very tall block above
  // the pedal grid, so the page has to go back to the top or the user is left at
  // an offset that now points somewhere unrelated. A plain window.scrollTo is not
  // enough: the scroll has to wait for the new layout to commit, and on iOS the
  // WebView restores its own offset afterwards. scrollPageToTop handles both.
  const scrollCancelRef = useRef(null);
  const returnToTop = () => {
    scrollCancelRef.current?.();
    scrollCancelRef.current = scrollPageToTop();
  };
  useEffect(() => () => scrollCancelRef.current?.(), []);

  const handleEdit = (pedal) => {
    // 🔒 TRADE PROTECTION: Check if pedal is in an active trade before allowing editing
    if (pedal?.inTrade) {
      alert('❌ Cannot edit this pedal because it is currently involved in an active trade. Please wait for the trade to complete or be cancelled before editing.');
      return;
    }
    
    setEditingPedal(pedal);
    returnToTop();
  };

  const handleFinishEdit = () => {
    setEditingPedal(null);
    // Always drop the photo-step focus, or the next ordinary Edit in this visit
    // would scroll to the photos instead of the top of the form.
    setEditFocus(null);
    if (editReturnTo) {
      // Came from an offer's photo gate: go straight back to that offer.
      const dest = editReturnTo;
      setEditReturnTo(null);
      navigate(dest);
      return;
    }
    returnToTop();
  };

  useEffect(() => {
    let unsubscribe = null;

    const setupListingsListener = (user) => {
      if (!user) {
        setListings([]);
        setListingsLoading(false);
        return;
      }

      // Reset loading state when setting up a new listener
      setListingsLoading(true);

      const q = query(
        collection(db, 'listings'),
        where('ownerId', '==', user.uid)
      );

      unsubscribe = onSnapshot(q, (snapshot) => {
        const items = snapshot.docs
          .map((doc) => ({
            id: doc.id,
            ...doc.data()
          }))
          // Filter out pedals hidden from pedalboard (replaced by reactivated versions)
          .filter(item => item.hiddenFromPedalboard !== true);
        setListings(items);
        setListingsLoading(false);
      });
    };

    // Set up listener for current user
    setupListingsListener(auth.currentUser);

    // Also listen for auth state changes to handle refresh scenarios
    const authUnsubscribe = auth.onAuthStateChanged((user) => {
      if (unsubscribe) {
        unsubscribe();
        unsubscribe = null;
      }
      setupListingsListener(user);
    });

    return () => {
      if (unsubscribe) unsubscribe();
      authUnsubscribe();
    };
  }, [editingPedal]);

  // Deep link from the offer page's photo gate (plan §4, 2026-10-18):
  // /pedalboard?edit=<listingId> opens the inline edit form for that listing
  // once the owner's listings have loaded, then drops the param so a refresh
  // or back-swipe does not reopen it. Honors the same inTrade lock as the
  // Edit button.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const editId = params.get('edit');
    if (!editId || editingPedal || listings.length === 0) return;
    const target = listings.find((l) => l.id === editId);
    if (!target) return;
    if (target.inTrade) {
      navigate(location.pathname, { replace: true });
      return;
    }
    const focusPhotos = params.get('step') === 'photos';
    const back = params.get('returnTo');
    // Only in-app paths; never an absolute URL from the query string.
    setEditReturnTo(back && back.startsWith('/') && !back.startsWith('//') ? back : null);
    setEditFocus(focusPhotos ? 'photos' : null);
    setEditingPedal(target);
    // The animated scroll-to-top would fight the form's own scroll to the photo
    // section (it keeps animating for a few hundred ms); let the form own it.
    if (!focusPhotos) returnToTop();
    navigate(location.pathname, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- runs when listings land or the URL changes
  }, [location.search, listings, editingPedal]);

  const refreshListings = useCallback(async () => {
    const user = auth.currentUser;
    if (!user) return;
    setListingsLoading(true);
    try {
      const q = query(
        collection(db, 'listings'),
        where('ownerId', '==', user.uid)
      );
      const snapshot = await getDocs(q);
      const items = snapshot.docs
        .map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }))
        .filter((item) => item.hiddenFromPedalboard !== true);
      setListings(items);
    } finally {
      setListingsLoading(false);
    }
  }, []);

  useRegisterPullToRefresh(refreshListings);

  // 🔍 SYNC PEDAL STATUS: Check if pedals should be marked as inTrade based on existing trades
  useEffect(() => {
    const syncPedalTradeStatus = async () => {
      const user = auth.currentUser;
      if (!user) return;

      try {
        console.log('🔍 Syncing pedal trade status for existing trades...');
        
        // Query for ONLY accepted or inProgress trades (explicitly exclude completed)
        // 🔧 BUG FIX: Only sync truly active trades, never completed ones
        const initiatorTradesQuery = query(
          collection(db, 'tradeOffers'),
          where('fromUserId', '==', user.uid),
          where('status', 'in', ['accepted', 'inProgress'])
        );
        
        // Query for ONLY accepted or inProgress trades (explicitly exclude completed)
        // 🔧 BUG FIX: Only sync truly active trades, never completed ones
        const responderTradesQuery = query(
          collection(db, 'tradeOffers'),
          where('toUserId', '==', user.uid),
          where('status', 'in', ['accepted', 'inProgress'])
        );
        
        const [initiatorSnapshot, responderSnapshot] = await Promise.all([
          getDocs(initiatorTradesQuery),
          getDocs(responderTradesQuery)
        ]);
        
        const userTrades = [
          ...initiatorSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })),
          ...responderSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        ];
        
        console.log('🔍 Found existing accepted/inProgress trades:', userTrades.length);
        
        if (userTrades.length > 0) {
          const batch = writeBatch(db);
          let pedalUpdates = 0;
          
          for (const trade of userTrades) {
            // 🔧 BUG FIX: Skip trades that are actually completed (transfers successful)
            const isTradeCompleted = trade.transferStatus?.status === 'success' || 
                                   trade.status === 'completed' || 
                                   trade.status === 'complete';
            
            if (isTradeCompleted) {
              console.log(`⏭️ Skipping completed trade ${trade.id} - should not mark pedals as inTrade`);
              continue;
            }
            
            // Mark all initiator pedals as inTrade if they aren't already
            const allInitiatorIds = getInitiatorPedalIds(trade);
            for (const pedalId of allInitiatorIds) {
              const pedalRef = doc(db, 'listings', pedalId);
              const pedalDoc = await getDoc(pedalRef);
              
              const pedalData = pedalDoc.data();
              const pedalAlreadyTraded = pedalData?.tradeCompletedAt;
              
              if (pedalDoc.exists() && !pedalData.inTrade && pedalData.ownerId === user.uid && !pedalAlreadyTraded) {
                batch.update(pedalRef, {
                  inTrade: true,
                  tradeAcceptedAt: trade.updatedAt || trade.createdAt,
                  tradeId: trade.id
                });
                pedalUpdates++;
                console.log(`🔒 Marking user's initiator pedal ${pedalId} as inTrade for trade ${trade.id}`);
              } else if (pedalAlreadyTraded) {
                console.log(`⏭️ Skipping pedal ${pedalId} - already has tradeCompletedAt`);
              }
            }
            
            if (trade.responderPedalId) {
              const pedalRef = doc(db, 'listings', trade.responderPedalId);
              const pedalDoc = await getDoc(pedalRef);
              
              if (pedalDoc.exists() && !pedalDoc.data().inTrade && pedalDoc.data().ownerId === user.uid) {
                batch.update(pedalRef, {
                  inTrade: true,
                  tradeAcceptedAt: trade.updatedAt || trade.createdAt,
                  tradeId: trade.id
                });
                pedalUpdates++;
                console.log(`🔒 Marking user's responder pedal ${trade.responderPedalId} as inTrade for trade ${trade.id}`);
              }
            }
          }
          
          if (pedalUpdates > 0) {
            await batch.commit();
            console.log(`✅ Synced ${pedalUpdates} pedals to inTrade status`);
          } else {
            console.log('✅ All pedals already have correct trade status');
          }
        } else {
          console.log('✅ No accepted/inProgress trades found for this user');
        }
        
      } catch (error) {
        console.error('❌ Error syncing pedal trade status:', error);
      }
    };

    syncPedalTradeStatus();
  }, []); // Run once on component mount

  useEffect(() => {
    if (!currentUser || listings.length === 0) return;

    const fetchMatchCounts = async () => {
      try {
        const [asMyPedalSnap, asMatchedPedalSnap] = await Promise.all([
          getDocs(query(collection(db, 'matches'), where('myPedalUser', '==', currentUser.uid))),
          getDocs(query(collection(db, 'matches'), where('matchedPedalUser', '==', currentUser.uid)))
        ]);

        const seen = new Set();
        const counts = {};

        for (const matchDoc of asMyPedalSnap.docs) {
          if (seen.has(matchDoc.id)) continue;
          seen.add(matchDoc.id);
          const pedalId = matchDoc.data().myPedal?.id;
          if (pedalId) counts[pedalId] = (counts[pedalId] || 0) + 1;
        }

        for (const matchDoc of asMatchedPedalSnap.docs) {
          if (seen.has(matchDoc.id)) continue;
          seen.add(matchDoc.id);
          const pedalId = matchDoc.data().matchedPedal?.id;
          if (pedalId) counts[pedalId] = (counts[pedalId] || 0) + 1;
        }

        setMatchCounts(counts);
      } catch (err) {
        console.error('Failed to fetch match counts:', err);
      }
    };

    fetchMatchCounts();
  }, [currentUser, listings]);

  const applyFilters = (items) => {
    const filtered = items.filter((item) => {
      const { showActive, minPrice, maxPrice } = filters;

      // Handle cases where active might be undefined/null
      const isActive = item.active === true;
      
      const isInPurchase = item.inTrade && item.purchaseOfferId;
      const isInTrade = item.inTrade && !item.purchaseOfferId;
      
      const isActiveOk =
        showActive === 'all' ||
        (showActive === 'active' && isActive && !item.inTrade) ||
        (showActive === 'inactive' && !isActive && !item.inTrade) ||
        (showActive === 'inTrade' && isInTrade) ||
        (showActive === 'inPurchase' && isInPurchase);

      const isPriceOk =
        (!minPrice || item.price >= Number(minPrice)) &&
        (!maxPrice || item.price <= Number(maxPrice));



      // 🔄 NEW REACTIVATION LOGIC: Hide pedals that were replaced by reactivation
      if (item.hiddenFromPedalboard) {
        return false;
      }

      return isActiveOk && isPriceOk;
    });

    return filtered.sort((a, b) => {
      const getPriority = (pedal) => {
        if (pedal.needsReactivation) return 5;
        if (pedal.inTrade && pedal.purchaseOfferId) return 3;
        if (pedal.inTrade) return 2;
        if (pedal.active) return 1;
        return 4; // Inactive
      };

      const priorityA = getPriority(a);
      const priorityB = getPriority(b);

      if (priorityA !== priorityB) return priorityA - priorityB;

      const getTime = (pedal) => {
        if (!pedal.createdAt) return 0;
        if (pedal.createdAt.toDate) return pedal.createdAt.toDate().getTime();
        if (pedal.createdAt.seconds) return pedal.createdAt.seconds * 1000;
        return new Date(pedal.createdAt).getTime();
      };

      return getTime(b) - getTime(a);
    });
  };

  if (!authLoading && !currentUser) {
    return (
      <div style={{ position: 'relative', minHeight: '100vh', overflow: 'hidden' }}>
        <style>{`
          @keyframes modalPulse {
            0%, 100% { box-shadow: 0 8px 32px rgba(90, 158, 203, 0.3); }
            50% { box-shadow: 0 8px 48px rgba(90, 158, 203, 0.6); }
          }
          @media (max-width: 768px) {
            .preview-modal {
              max-width: 240px !important;
              padding: 1rem 1rem !important;
            }
            .preview-modal h2 { font-size: 1.05rem !important; }
            .preview-modal p { font-size: 0.8rem !important; margin-bottom: 0.75rem !important; }
            .preview-modal a.preview-signup-btn { font-size: 14px !important; padding: 8px 24px !important; }
            .preview-modal p.preview-modal-login { font-size: 0.8rem !important; margin: 0.25rem 0 0 0 !important; }
          }
        `}</style>
        <picture>
          <source media="(max-width: 768px)" srcSet="/images/pedalboard-preview-mobile.png?v=5" />
          <img
            src="/images/pedalboard-preview-desktop.png?v=2"
            alt="Pedalboard preview"
            style={{ width: '100%', display: 'block' }}
          />
        </picture>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backdropFilter: 'blur(3px)',
          WebkitBackdropFilter: 'blur(3px)',
          pointerEvents: 'none'
        }} />
        <div className="preview-modal" style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 100,
          backgroundColor: 'rgba(255, 255, 255, 0.80)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderRadius: '16px',
          padding: '2.5rem 2rem',
          maxWidth: '420px',
          width: '90%',
          textAlign: 'center',
          fontFamily: 'Nunito, sans-serif',
          animation: 'modalPulse 3s ease-in-out infinite',
          border: '2px solid rgba(224, 224, 224, 0.7)'
        }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#374151', marginBottom: '0.75rem' }}>
            Your Pedalboard Awaits
          </h2>
          <p style={{ fontSize: '1rem', color: '#6B7280', marginBottom: '1.5rem', lineHeight: '1.5' }}>
            Sign up to list your pedals, track their value, and start trading with musicians everywhere.
          </p>
          <Link className="preview-signup-btn" to="/signup" style={{
            display: 'inline-block',
            backgroundColor: '#C9A227',
            color: 'white',
            fontWeight: 'bold',
            fontSize: '16px',
            padding: '12px 32px',
            borderRadius: '9999px',
            textDecoration: 'none',
            fontFamily: 'Nunito, sans-serif',
            marginBottom: '1rem'
          }}>
            Sign Up Free
          </Link>
          <p className="preview-modal-login" style={{ fontSize: '0.9rem', color: '#6B7280' }}>
            Already have an account?{' '}
            <Link to="/login" style={{ color: '#5A9ECB', fontWeight: 'bold', textDecoration: 'none' }}>Log In</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'transparent', minHeight: 'auto', display: 'flex', flexDirection: 'column' }}>
      {/* Yellow background section */}
      <div style={{ backgroundColor: '#F7F3EB', flex: 1, padding: '0 1rem' }}>

      {isHubView && (
        <style>{`
          @media (min-width: 769px) {
            .pedalboard-hub-page-title {
              display: none !important;
            }
            .pedalboard-hub-add-cta {
              margin-bottom: 1.25rem !important;
            }
            .pedalboard-hub-filter-bar {
              max-width: 1100px !important;
              padding: 0 2rem !important;
              box-sizing: border-box !important;
            }
            .pedalboard-hub-grid {
              max-width: 1100px !important;
              padding: 0 2rem !important;
              box-sizing: border-box !important;
            }
          }
          @media (max-width: 768px) {
            .pedalboard-hub-expanded-header {
              display: none !important;
            }
            .pedalboard-hub-page-title {
              display: none !important;
            }
            .pedalboard-hub-filter-bar {
              display: none !important;
            }
          }
        `}</style>
      )}

      {/* Add a New Pedal CTA — standalone /pedalboard only. In the hub view this
          button is rendered inline with the header toggle (see setAddSlot). */}
      {!isHubView && !isLoading && isFullyVerified && (
        <div style={{ textAlign: 'center', paddingTop: '1rem', marginBottom: '0.5rem' }}>
          <Link
            to='/addpedal'
            className='inline-block'
            style={ADD_PEDAL_LINK_STYLE}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.75'; }}
            onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
          >
            + Add a New Pedal
          </Link>
        </div>
      )}

      <h1 className={['text-2xl font-bold text-center', isHubView && 'pedalboard-hub-page-title'].filter(Boolean).join(' ')} style={{ padding: '1rem 1rem 0.5rem 1rem' }}>My Pedalboard</h1>
      
      

      {/* Verification Check — only shown while loading status or when the
          account isn't yet fully verified. Verified users see no banner here;
          their "+ Add a New Pedal" CTA lives at the top under the toggle. */}
      {(isLoading || !isFullyVerified) && (
        <div className='mb-8'>
          {isLoading ? (
            <div className='p-3 border border-blue-300 rounded-lg bg-blue-50'>
              <p className='text-blue-800 text-sm'>
                ⏳ Loading verification status...
              </p>
            </div>
          ) : (
            <div className='p-3 border border-orange-300 rounded-lg bg-orange-50'>
              <p className='text-orange-800 text-sm mb-2'>
                ⚠️ Complete your account verification to add pedals to your pedalboard.
              </p>
              <p className='text-orange-700 text-xs mb-2'>
                Status: Email {currentUser?.emailVerified ? '✅' : '❌'} | Phone {firestoreUser?.phoneVerified ? '✅' : '❌'} | Account {firestoreUser?.isVerified ? '✅' : '❌'}
              </p>
              <Link to='/profile' className='text-orange-600 hover:underline text-sm font-medium'>
                → Complete Verification
              </Link>
            </div>
          )}
        </div>
      )}

      {editingPedal && (
        <div className='mb-4 mt-8'>
          <AddPedalForm editingPedal={editingPedal} onFinishEdit={handleFinishEdit} onOwnsHorizontalGesture={onOwnsHorizontalGesture} initialFocus={editFocus} />
        </div>
      )}
      


      {listingsLoading
        ? <p className='mt-8'>Loading your pedalboard…</p>
        : listings.length === 0 && <p className='mt-8'>No pedals listed yet.</p>}

      <div className={['pedalboard-filter-bar', isHubView && 'pedalboard-hub-filter-bar'].filter(Boolean).join(' ')} style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', maxWidth: '1400px', margin: '0 auto 2rem auto', padding: '0 1.5rem' }}>
        <select
          className="pedalboard-filter-select"
          value={filters.showActive}
          onChange={(e) => setFilters({ ...filters, showActive: e.target.value })}
          style={{
            borderRadius: '25px',
            fontSize: '0.9375rem',
            fontFamily: 'Nunito, sans-serif',
            fontWeight: 400,
            backgroundColor: 'white',
            color: '#374151',
            cursor: 'pointer',
            border: '1px solid #C89F3C',
            padding: '0.4rem 2rem 0.4rem 1rem',
            outline: 'none',
            appearance: 'none',
            WebkitAppearance: 'none',
            MozAppearance: 'none',
            backgroundImage: "url(\"data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%23C89F3C' d='M6 8L0 0h12z'/%3E%3C/svg%3E\")",
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right 1rem center',
            boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.1)'
          }}
        >
          <option value='all'>All</option>
          <option value='active'>Active</option>
          <option value='inactive'>Inactive</option>
          <option value='inTrade'>In Trade</option>
          <option value='inPurchase'>In Purchase</option>
        </select>
        <button
          type="button"
          onClick={handleBumpListings}
          disabled={bumpIsDisabled}
          className="pedalboard-action-btn pedalboard-bump-btn"
          title={
            !is18V
              ? 'Bump Listings is an 18V membership feature'
              : !bumpCooldown.canBump
                ? `Available again in ${bumpCooldown.daysRemaining > 0 ? `${bumpCooldown.daysRemaining} day${bumpCooldown.daysRemaining !== 1 ? 's' : ''}` : `${bumpCooldown.hoursRemaining}h`}`
                : 'Move all your active listings to the top of Browse & Matches'
          }
          style={{
            marginLeft: 'auto',
            backgroundColor: 'transparent',
            border: `1.5px solid ${bumpSuccess ? '#4db6ac' : bumpIsDisabled ? '#9ca3af' : '#C9A227'}`,
            borderRadius: '9999px',
            padding: '0.46rem 1.44rem',
            fontFamily: 'Nunito, sans-serif',
            color: bumpSuccess ? '#4db6ac' : bumpIsDisabled ? '#9ca3af' : '#C9A227',
            fontWeight: '600',
            fontSize: '1.08rem',
            cursor: bumpIsDisabled ? 'not-allowed' : 'pointer',
            whiteSpace: 'nowrap',
            opacity: bumpIsDisabled && !bumpSuccess ? 0.7 : 1,
          }}
        >
          {bumpButtonLabel()}
        </button>
        <button
          type="button"
          onClick={handleShare}
          className="pedalboard-action-btn pedalboard-share-btn"
          style={{
            marginLeft: '0.5rem',
            backgroundColor: 'transparent',
            border: '1.5px solid #5A9ECB',
            borderRadius: '9999px',
            padding: '0.46rem 1.44rem',
            fontFamily: 'Nunito, sans-serif',
            color: '#5A9ECB',
            fontWeight: '600',
            fontSize: '1.08rem',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            minWidth: '200px'
          }}
        >
          {copied ? 'Copied!' : 'Share My Pedalboard'}
        </button>
      </div>

      {isHubView && currentUser && (
        <div
          className="pedalboard-hub-action-row"
          style={{
            display: 'flex',
            gap: '0.5rem',
            maxWidth: '900px',
            margin: '0 auto 1.25rem auto',
            padding: '0 1.5rem',
            boxSizing: 'border-box',
          }}
        >
          <button
            type="button"
            onClick={handleBumpListings}
            disabled={bumpIsDisabled}
            className="pedalboard-action-btn"
            style={{
              ...HUB_ACTION_BTN_STYLE,
              border: `1.5px solid ${bumpSuccess ? '#4db6ac' : bumpIsDisabled ? '#9ca3af' : '#C9A227'}`,
              color: bumpSuccess ? '#4db6ac' : bumpIsDisabled ? '#9ca3af' : '#C9A227',
              cursor: bumpIsDisabled ? 'not-allowed' : 'pointer',
            }}
          >
            {bumpButtonLabel()}
          </button>
          <button
            type="button"
            onClick={handleShare}
            className="pedalboard-action-btn"
            style={{
              ...HUB_ACTION_BTN_STYLE,
              border: '1.5px solid #5A9ECB',
              color: '#5A9ECB',
            }}
          >
            {copied ? 'Copied!' : 'Share My Pedalboard'}
          </button>
        </div>
      )}

      <div className={['pedalboard-grid', isHubView && 'pedalboard-hub-grid'].filter(Boolean).join(' ')} style={{ 
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
        maxWidth: '900px',
        margin: '0 auto',
        padding: '0 1.5rem'
      }}>
      <style jsx>{`
        @media (min-width: 1600px) {
          .pedalboard-grid:not(.pedalboard-hub-grid) {
            max-width: 1000px !important;
          }
          .pedalboard-filter-bar:not(.pedalboard-hub-filter-bar) {
            max-width: 1600px !important;
          }
        }
        @media (max-width: 768px) {
          .pedalboard-grid {
            max-width: 100% !important;
            padding: 0 0.15rem !important;
          }
          .pedalboard-filter-bar {
            max-width: 100% !important;
            padding: 0 !important;
          }
          .pedalboard-share-btn {
            font-size: 0.8rem !important;
            padding: 0.35rem 1.05rem !important;
          }
          .pedalboard-hub-action-row {
            padding: 0 0.15rem !important;
          }
        }
        @media (min-width: 769px) {
          .pedalboard-hub-action-row {
            display: none !important;
          }
        }
        /* Desktop only: upsize the listing-type dropdown to match the Browse
           page's desktop category dropdown (All / Pedals / Accessories). Mobile
           keeps the smaller default sizing from the inline style above. */
        @media (min-width: 769px) {
          .pedalboard-filter-select {
            font-size: 1.125rem !important;
            font-weight: 700 !important;
            padding: 0.75rem 2.75rem 0.75rem 1.5rem !important;
            background-position: right 1rem center !important;
          }
        }
        .pedalboard-match-banner {
          background-color: rgb(201, 162, 39);
          box-sizing: border-box;
          display: flex;
          align-items: flex-start;
          padding: 6px 12px;
          padding-bottom: 6px;
          transition: padding-bottom 0.15s ease;
        }
        .pedalboard-match-banner:hover {
          padding-bottom: 12px;
        }
        .pedalboard-match-banner:active {
          padding-bottom: 8px;
        }
        .pedalboard-match-banner-inner {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          transition: transform 0.15s ease;
        }
        .pedalboard-match-banner:hover .pedalboard-match-banner-inner {
          transform: translateY(5px);
        }
        .pedalboard-match-banner:active .pedalboard-match-banner-inner {
          transform: translateY(2px);
        }
        .pedalboard-action-btn {
          transition: transform 0.15s ease;
        }
        .pedalboard-action-btn:hover:not(:disabled) {
          transform: translateY(-2px);
        }
        .pedalboard-action-btn:active:not(:disabled) {
          transform: translateY(0);
        }
      `}</style>
        {applyFilters(listings).map((pedal) => (
          <div key={pedal.id} style={{
            borderRadius: '14px',
            backgroundColor: 'white',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)',
            border: '1px solid #e5e7eb',
            overflow: 'hidden',
            fontFamily: 'Nunito, sans-serif'
          }}>
            {/* ── Row 1: thumbnail + details ───────────────────────────── */}
            <div style={{ display: 'flex', alignItems: 'stretch' }}>

              {/* Thumbnail — fixed square with a white inset edge so the image reads
                  as a rounded square (matching the site's rounded design language)
                  rather than a flush, partially-cropped block. */}
              <div style={{
                position: 'relative',
                width: '130px',
                minWidth: '130px',
                height: '130px',
                boxSizing: 'border-box',
                padding: '8px 0 0 8px',
                backgroundColor: 'white',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden'
              }}>
                {/* The photo opens the listing, same destination as the View
                    listing button below (owner, 2026-11-01; mirrors the offer
                    and match pages). Photo only: the match banner pinned over
                    the bottom edge keeps its own link to the matches page. */}
                {pedal.images?.top ? (
                  <Link to={`/listing/${pedal.id}`} aria-label={`View listing: ${pedal.make} ${pedal.model}`}
                    style={{ display: 'block', width: '100%', height: '100%', lineHeight: 0, borderRadius: '10px', overflow: 'hidden' }}>
                    <ListingThumb
                      src={pedal.images.top}
                      alt="Top view"
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', borderRadius: '10px' }}
                    />
                  </Link>
                ) : isPlaceholderListing(pedal) ? (
                  <Link to={`/listing/${pedal.id}`} aria-label={`View listing: ${pedal.make} ${pedal.model}`}
                    style={{ display: 'block', width: '100%', height: '100%', lineHeight: 0, borderRadius: '10px', overflow: 'hidden' }}>
                    <ListingThumb
                      src={placeholderFor(pedal).url}
                      fallbackSrc={SKETCH_PLACEHOLDER_URL}
                      alt="Photos coming"
                      style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', borderRadius: '10px', opacity: 0.85 }}
                    />
                  </Link>
                ) : (
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#B8B0A0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="2" width="20" height="20" rx="2.5" />
                    <circle cx="8" cy="8" r="2" /><circle cx="16" cy="8" r="2" />
                    <circle cx="8" cy="16" r="2" /><circle cx="16" cy="16" r="2" />
                  </svg>
                )}

                {/* Trade match banner — pinned to the bottom of the thumbnail,
                    only when the pedal has 1+ active matches. Clickable,
                    links to the matches page. */}
                {(matchCounts[pedal.id] || 0) > 0 && (
                  <Link
                    to="/matches"
                    title="View your trade matches"
                    onClick={(e) => e.stopPropagation()}
                    className="pedalboard-match-banner"
                    style={{
                      position: 'absolute', bottom: 0, left: '8px', right: 0,
                      borderBottomLeftRadius: '10px', borderBottomRightRadius: '10px',
                      overflow: 'hidden',
                      zIndex: 2, textDecoration: 'none', cursor: 'pointer', color: 'inherit'
                    }}
                  >
                    <span className="pedalboard-match-banner-inner">
                      <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#fff', display: 'inline-block', flexShrink: 0 }} />
                      <span style={{ color: '#fff', fontWeight: 700, fontSize: '0.85rem', fontFamily: 'Nunito, sans-serif', whiteSpace: 'nowrap' }}>
                        {matchCounts[pedal.id]} match{matchCounts[pedal.id] !== 1 ? 'es' : ''}
                      </span>
                    </span>
                  </Link>
                )}
              </div>

              {/* Info pane — brand + model pinned to top, then condition +
                  status badge row, then price below */}
              <div style={{ flex: 1, minWidth: 0, padding: '12px 14px 12px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', gap: '5px' }}>
                {/* Brand */}
                <div style={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#9ca3af', lineHeight: 1.2, textAlign: 'left' }}>
                  {pedal.make}
                </div>
                {/* Model */}
                <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1f2937', lineHeight: 1.25, textAlign: 'left' }}>
                  {pedal.model}
                </div>
                {/* Condition pill + status badge on the same row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', marginTop: '2px' }}>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center',
                    padding: '3px 10px', borderRadius: '9999px',
                    backgroundColor: '#E5DDC9', color: '#5C4A2A',
                    fontSize: '0.78rem', fontWeight: 700, whiteSpace: 'nowrap'
                  }}>
                    {pedal.condition}
                  </span>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center',
                    padding: '3px 10px', borderRadius: '9999px',
                    border: `1.5px solid ${pedal.needsReactivation ? 'rgba(172,67,32,0.7)' : pedal.inTrade ? 'rgba(234,140,0,0.7)' : pedal.active ? 'rgba(46,125,50,0.7)' : 'rgba(172,67,32,0.7)'}`,
                    backgroundColor: pedal.needsReactivation ? 'rgba(172,67,32,0.08)' : pedal.inTrade ? 'rgba(234,140,0,0.08)' : pedal.active ? 'rgba(46,125,50,0.08)' : 'rgba(172,67,32,0.08)',
                    color: pedal.needsReactivation ? '#ac4320' : pedal.inTrade ? '#b45309' : pedal.active ? '#166534' : '#ac4320',
                    fontSize: '0.75rem', fontWeight: 700, whiteSpace: 'nowrap'
                  }}>
                    {pedal.needsReactivation
                      ? (pedal.tradeCompletedAt ? 'Traded' : pedal.purchaseCompletedAt ? 'Purchased' : 'Needs Reactivation')
                      : pedal.inTrade
                        ? (pedal.purchaseOfferId ? 'In Purchase' : 'In Trade')
                        : pedal.active ? '● Active' : 'Inactive'}
                  </span>
                  {/* Offer mode chip (plan §5): what kinds of offers this listing takes. */}
                  <span style={{
                    display: 'inline-flex', alignItems: 'center',
                    padding: '3px 10px', borderRadius: '9999px',
                    border: `1.5px solid ${offerModeOf(pedal) === 'trade' ? 'rgba(200,159,60,0.7)' : offerModeOf(pedal) === 'sell' ? 'rgba(90,158,203,0.7)' : 'rgba(107,114,128,0.5)'}`,
                    backgroundColor: offerModeOf(pedal) === 'trade' ? 'rgba(200,159,60,0.1)' : offerModeOf(pedal) === 'sell' ? 'rgba(90,158,203,0.1)' : 'rgba(107,114,128,0.06)',
                    color: offerModeOf(pedal) === 'trade' ? '#7C5A12' : offerModeOf(pedal) === 'sell' ? '#2F6B94' : '#4B5563',
                    fontSize: '0.75rem', fontWeight: 700, whiteSpace: 'nowrap'
                  }}>
                    {OFFER_MODE_LABELS[offerModeOf(pedal)]}
                  </span>
                </div>
                {/* Price — its own row below condition + status */}
                <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#C89F3C', textAlign: 'left', marginTop: '2px' }}>
                  ${pedal.price}
                </div>
                {/* Warning notes */}
                {pedal.needsReactivation && !pedal.tradeCompletedAt && !pedal.purchaseCompletedAt && (
                  <div style={{ color: '#ac4320', fontWeight: 700, fontSize: '0.72rem' }}>⚠️ Needs Reactivation</div>
                )}
                {pedal.inTrade && !pedal.needsReactivation && (
                  <div style={{ color: '#b45309', fontWeight: 700, fontSize: '0.72rem' }}>
                    🔒 {pedal.purchaseOfferId ? 'In Active Purchase' : 'In Active Trade'}
                  </div>
                )}
              </div>
            </div>

            {/* ── Row 2: action buttons ─────────────────────────────────── */}
            <div style={{
              display: 'flex', gap: '0.5rem',
              padding: '10px 14px 12px',
              borderTop: '1px solid #f3f4f6'
            }}>
              <Link
                to={`/listing/${pedal.id}`}
                className="pedalboard-action-btn"
                style={{
                  backgroundColor: '#E5E7EB', borderRadius: '9999px', border: 'none',
                  padding: '8px 16px', fontFamily: 'Nunito, sans-serif',
                  color: '#374151', cursor: 'pointer', fontSize: '14px', fontWeight: 600,
                  flex: '1 1 0', textDecoration: 'none', textAlign: 'center',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box'
                }}
              >
                Listing
              </Link>
              <button
                type="button"
                onClick={() => handleEdit(pedal)}
                disabled={pedal.inTrade || sellingDisabled}
                className="pedalboard-action-btn"
                style={{
                  backgroundColor: (pedal.inTrade || sellingDisabled) ? '#9CA3AF' : '#C9A227',
                  borderRadius: '9999px', border: 'none',
                  padding: '8px 16px', fontFamily: 'Nunito, sans-serif',
                  color: 'white', fontWeight: 700,
                  cursor: (pedal.inTrade || sellingDisabled) ? 'not-allowed' : 'pointer',
                  fontSize: '14px', flex: '1 1 0',
                  opacity: (pedal.inTrade || sellingDisabled) ? 0.6 : 1,
                  boxSizing: 'border-box', textAlign: 'center'
                }}
                title={
                  pedal.inTrade ? 'Cannot edit — pedal is in an active trade' :
                  sellingDisabled ? 'Selling, trading & listing are paused. Pay outstanding invoice to restore your account.' :
                  'Edit this pedal'
                }
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => handleDelete(pedal.id)}
                disabled={sellingDisabled}
                className="pedalboard-action-btn"
                style={{
                  backgroundColor: sellingDisabled ? '#9CA3AF' : '#E5E7EB',
                  borderRadius: '9999px', border: 'none',
                  padding: '8px 16px', fontFamily: 'Nunito, sans-serif',
                  color: sellingDisabled ? '#fff' : '#374151', fontWeight: 600,
                  cursor: sellingDisabled ? 'not-allowed' : 'pointer',
                  opacity: sellingDisabled ? 0.6 : 1,
                  fontSize: '14px', flex: '1 1 0',
                  boxSizing: 'border-box', textAlign: 'center'
                }}
                title={sellingDisabled ? 'Selling, trading & listing are paused. Pay outstanding invoice to restore your account.' : undefined}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
      
      {/* Fixed gap below content */}
      <div style={{ height: '200px' }}></div>
      </div>
      
      {/* Footer */}
      <Footer />

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="back-to-top-btn"
          style={{
            position: 'fixed',
            bottom: window.innerWidth <= 768 ? '90px' : '30px',
            right: '30px',
            backgroundColor: '#5A9ECB',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '36px',
            height: '36px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'system-ui, -apple-system, sans-serif'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#4A8AB0';
            e.target.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#5A9ECB';
            e.target.style.transform = 'scale(1)';
          }}
          aria-label="Back to top"
        >
          ⌃
        </button>
      )}

      {showEarlyBirdModal && (
        <div
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '1rem'
          }}
          onClick={handleDismissEarlyBird}
        >
          <div
            style={{
              backgroundColor: '#fff',
              borderRadius: '16px',
              padding: '2rem 1.75rem',
              maxWidth: '420px',
              width: '100%',
              textAlign: 'center',
              fontFamily: 'Nunito, sans-serif',
              position: 'relative',
              boxShadow: '0 12px 40px rgba(0,0,0,0.2)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={handleDismissEarlyBird}
              style={{
                position: 'absolute',
                top: '12px',
                right: '16px',
                background: 'none',
                border: 'none',
                fontSize: '1.5rem',
                cursor: 'pointer',
                color: '#9ca3af',
                lineHeight: 1
              }}
              aria-label="Close"
            >
              ×
            </button>

            <h2 style={{
              fontSize: '1.35rem',
              fontWeight: '700',
              color: '#374151',
              marginBottom: '0.75rem'
            }}>
              Early Bird Bonus!
            </h2>

            <p style={{
              fontSize: '1rem',
              color: '#4A5759',
              lineHeight: '1.6',
              marginBottom: '0.5rem'
            }}>
              List your first pedal within 7 days of signing up and your <strong>platform trade fee is waived</strong> on your first trade!
            </p>

            <p style={{
              fontSize: '0.95rem',
              color: '#C9A227',
              fontWeight: '700',
              marginBottom: '1.25rem'
            }}>
              You have {earlyBirdDaysRemaining} day{earlyBirdDaysRemaining === 1 ? '' : 's'} remaining
            </p>

            <button
              onClick={handleEarlyBirdCTA}
              style={{
                backgroundColor: '#C9A227',
                color: '#fff',
                border: 'none',
                borderRadius: '9999px',
                padding: '12px 32px',
                fontSize: '1rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                fontFamily: 'Nunito, sans-serif',
                transition: 'opacity 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.opacity = '0.85'}
              onMouseLeave={(e) => e.target.style.opacity = '1'}
            >
              List a Pedal
            </button>
          </div>
        </div>
      )}

      {confirmDeletePedal && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999, padding: '1rem'
          }}
          onClick={() => setConfirmDeletePedal(null)}
        >
          <div
            style={{
              backgroundColor: '#fff', borderRadius: '16px',
              padding: '2rem 1.75rem', maxWidth: '380px', width: '100%',
              textAlign: 'center', fontFamily: 'Nunito, sans-serif',
              position: 'relative', boxShadow: '0 12px 40px rgba(0,0,0,0.2)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setConfirmDeletePedal(null)}
              style={{
                position: 'absolute', top: '12px', right: '16px',
                background: 'none', border: 'none', fontSize: '1.5rem',
                cursor: 'pointer', color: '#9ca3af', lineHeight: 1
              }}
              aria-label="Close"
            >
              ×
            </button>
            <h2 style={{ fontSize: '1.2rem', fontWeight: 800, color: '#1f2937', marginBottom: '0.5rem' }}>
              Delete Listing?
            </h2>
            <p style={{ fontSize: '0.95rem', color: '#4b5563', marginBottom: '1.5rem', lineHeight: 1.55 }}>
              Are you sure you want to permanently delete{' '}
              <strong>
                {confirmDeletePedal.make && confirmDeletePedal.model
                  ? `${confirmDeletePedal.make} ${confirmDeletePedal.model}`
                  : 'this pedal'}
              </strong>
              {confirmDeletePedal.price ? ` ($${confirmDeletePedal.price})` : ''}?
              <br />
              <span style={{ fontSize: '0.85rem', color: '#9ca3af' }}>This action cannot be undone.</span>
            </p>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={() => setConfirmDeletePedal(null)}
                style={{
                  flex: 1, backgroundColor: '#E5E7EB', color: '#374151',
                  border: 'none', borderRadius: '9999px', padding: '0.65rem 1rem',
                  fontWeight: 600, fontSize: '0.95rem', cursor: 'pointer',
                  fontFamily: 'Nunito, sans-serif'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                style={{
                  flex: 1, backgroundColor: '#dc2626', color: 'white',
                  border: 'none', borderRadius: '9999px', padding: '0.65rem 1rem',
                  fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer',
                  fontFamily: 'Nunito, sans-serif'
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {showBumpUpsellModal && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999, padding: '1rem'
          }}
          onClick={() => setShowBumpUpsellModal(false)}
        >
          <div
            style={{
              backgroundColor: '#fff', borderRadius: '16px',
              padding: '2rem 1.75rem', maxWidth: '400px', width: '100%',
              textAlign: 'center', fontFamily: 'Nunito, sans-serif',
              position: 'relative', boxShadow: '0 12px 40px rgba(0,0,0,0.2)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setShowBumpUpsellModal(false)}
              style={{
                position: 'absolute', top: '12px', right: '16px',
                background: 'none', border: 'none', fontSize: '1.5rem',
                cursor: 'pointer', color: '#9ca3af', lineHeight: 1
              }}
              aria-label="Close"
            >
              ×
            </button>
            <h2 style={{ fontSize: '1.3rem', fontWeight: 800, color: '#1f2937', marginBottom: '0.5rem' }}>
              Bump Listings
            </h2>
            <p style={{ fontSize: '0.95rem', color: '#4b5563', marginBottom: '1rem', lineHeight: 1.55 }}>
              <strong>Bump Listings</strong> moves all of your active listings to the top of Browse,
              Wantlist Matches, and Match Opportunities — giving them maximum visibility without
              changing the price.
            </p>
            <p style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '1.5rem', lineHeight: 1.5 }}>
              Available once every 7 days, this is an exclusive <strong>18V membership</strong> feature.
            </p>
            <Link
              to="/profile#membership-section"
              onClick={() => setShowBumpUpsellModal(false)}
              style={{
                display: 'block', backgroundColor: '#C9A227', color: 'white',
                borderRadius: '9999px', padding: '0.65rem 1.5rem',
                fontWeight: 700, fontSize: '1rem', textDecoration: 'none',
                marginBottom: '0.75rem'
              }}
            >
              Upgrade to 18V
            </Link>
            <button
              onClick={() => setShowBumpUpsellModal(false)}
              style={{
                background: 'none', border: 'none', color: '#9ca3af',
                fontSize: '0.9rem', cursor: 'pointer', fontFamily: 'Nunito, sans-serif'
              }}
            >
              Maybe later
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Pedalboard;
